/**
 * Shared, presentation-only renderer for a FormSpec. Used by the public form
 * (client/pages/public-form.ts) and the builder's live preview
 * (client/pages/form-builder.ts) so both render identically.
 *
 * Spec types mirror src/forms/spec.ts (the server is the source of truth); kept
 * here because client/ and src/ are separate build roots.
 */
import { esc, type Branding } from './brand.js';
import { icon } from './icons.js';

export interface FieldOption { key: string; label: string }
export type FieldType = 'text' | 'email' | 'tel' | 'date' | 'checkbox-group' | 'consent';
export interface SpecField {
  key: string; type: FieldType; label: string; required: boolean; hidden: boolean;
  order: number; binding: 'system' | 'custom'; locked?: boolean; removable?: boolean; options?: FieldOption[];
}
export interface SpecSection { id: string; title: string; subtitle?: string; order: number; fields: SpecField[] }
export interface FormSpec { version: number; sections: SpecSection[]; content: { whyChooseUs: string[] } }

export interface RenderCtx {
  agencyName: string;
  companyName: string;
  branding: Branding;
  /**
   * The consent sentence, as served by GET /api/public/:token (forms.ConsentText
   * in Go). Rendered verbatim rather than interpolated here, so the wording an
   * employee reads is the same string the server snapshots onto the submission
   * and later exports to PDF.
   *
   * Optional only for the builder's live preview, which has no token to fetch
   * and falls back to the same shape locally.
   */
  consentText?: string;
}

export const SCALAR_TYPES: FieldType[] = ['text', 'email', 'tel', 'date'];

/** Sections in display order with visible fields sorted; mirrors visibleSections() server-side. */
export function visibleSections(spec: FormSpec): SpecSection[] {
  return [...spec.sections]
    .sort((a, b) => a.order - b.order)
    .map((s) => ({ ...s, fields: [...s.fields].filter((f) => !f.hidden).sort((a, b) => a.order - b.order) }));
}

/** The inner HTML for the form card body: sections, fields, consent. */
export function renderSections(spec: FormSpec, ctx: RenderCtx): string {
  return visibleSections(spec).map((s) => renderSection(s, ctx)).join('');
}

/** The <li> items for the "Why Choose Us" sidebar. */
export function renderWhyItems(spec: FormSpec): string {
  return spec.content.whyChooseUs.map((w) => `<li>${icon('check')}<span>${esc(w)}</span></li>`).join('');
}

function renderSection(sec: SpecSection, ctx: RenderCtx): string {
  if (!sec.fields.length) return '';
  const consentOnly = sec.fields.every((f) => f.type === 'consent');
  const scalars = sec.fields.filter((f) => SCALAR_TYPES.includes(f.type));
  let html = '';
  if (!consentOnly) html += `<h2 style="color:var(--brand-primary);margin-top:26px">${esc(sec.title)}</h2>`;
  if (sec.subtitle) html += `<p class="muted" style="margin-top:-4px">${esc(sec.subtitle)}</p>`;
  if (scalars.length) html += `<div class="grid-2">${scalars.map((f) => renderScalar(f, ctx)).join('')}</div>`;
  for (const f of sec.fields) {
    if (f.type === 'checkbox-group') html += renderGroup(f, sec.title !== f.label);
    else if (f.type === 'consent') html += renderConsent(ctx);
  }
  return html;
}

function renderScalar(f: SpecField, ctx: RenderCtx): string {
  if (f.locked) {
    const val = f.key === 'company_name' ? ctx.companyName : '';
    return `<div class="field">
      <label>${esc(f.label)} ${icon('lock')}</label>
      <input type="text" value="${esc(val)}" readonly tabindex="-1" class="locked" />
      <span class="hint">Set by your employer for this form — can't be changed.</span></div>`;
  }
  const star = f.required ? ' *' : '';
  return `<div class="field"><label>${esc(f.label)}${star}</label><input type="${f.type}" id="${esc(f.key)}" ${f.required ? 'required' : ''} /></div>`;
}

function renderGroup(f: SpecField, showLabel: boolean): string {
  const cls = f.key === 'coverage' ? 'cov-list' : 'fs-list';
  const heading = showLabel
    ? `<label style="display:block;font-weight:600;margin:18px 0 8px">${esc(f.label)}${f.required ? ' *' : ''}</label>`
    : '';
  const items = (f.options ?? [])
    .map((o) => `<label class="check"><input type="checkbox" data-group="${esc(f.key)}" data-key="${esc(o.key)}" /><span>${esc(o.label)}</span></label>`)
    .join('');
  return `${heading}<div class="${cls}">${items}</div>`;
}

/**
 * Fallback wording for the builder preview only. Mirrors forms.ConsentText in
 * Go, which is authoritative — the public form never reaches this branch,
 * because the server always serves `consentText`.
 */
function fallbackConsentText(agencyName: string): string {
  return `I consent to ${agencyName || 'the agency'} contacting me about these benefits and to the processing of my information.`;
}

function renderConsent(ctx: RenderCtx): string {
  const b = ctx.branding || {};
  const text = ctx.consentText || fallbackConsentText(ctx.agencyName);
  // The privacy link sits outside the consent sentence, matching the server:
  // the stored snapshot is the agreement itself, and a link is a pointer to
  // terms rather than the terms.
  return `<label class="check" style="margin-top:24px"><input type="checkbox" id="consent" />
    <span>${esc(text)}${b.privacy_url ? ` <a href="${esc(b.privacy_url)}" target="_blank" rel="noopener">Privacy policy</a>.` : ''}</span></label>`;
}
